<template>
  <section class="d-flex flex-column ga-5">
    <div class="v-card-title">
      {{ t("components.companies.discounts.title") }}
      <v-chip v-if="unsavedChanges" color="warning">
        {{ t("components.companies.discounts.unsaved") }}
      </v-chip>
    </div>
    <div>
      <div class="d-flex ga-5 flex-wrap">
        <div
          v-for="(discount, index) in discounts"
          :key="index"
          :class="discount && isDiscountArchived(discount) ? 'd-none' : ''"
        >
          <div
            v-if="(!discount && addNewDiscount) || editDiscountIndex === index"
            class="bg-pink-4 elevation-2 rounded-lg pa-4 d-flex flex-column ga-2"
          >
            <y-select
              v-model="discountForm.type"
              :items="Object.values(DiscountTypeEnum)"
              hide-details
            />
            <y-select
              v-model="discountForm.product"
              :items="Object.values(DiscountProductEnum)"
              hide-details
            />
            <v-number-input
              v-model="discountForm.value"
              control-variant="stacked"
              :min="0"
              inset
              bg-color="white"
              rounded="lg"
              hide-details
            />
            <VueDatePicker v-model="discountForm.appliedUntil" :min-date="new Date()" />
            <div class="w-full d-flex justify-space-between">
              <y-btn-nd icon="mdi-delete" size="small" @click="deleteDiscount(index)" />
              <div class="d-flex ga-2">
                <y-btn-nd icon="mdi-close" size="small" @click="clearSelection" />
                <y-btn-nd
                  icon="mdi-check"
                  size="small"
                  :disabled="discountForm.value <= 0"
                  @click="updateDiscount(index)"
                />
              </div>
            </div>
          </div>

          <y-btn-nd v-else-if="!discount" block @click="selectDiscount(index)">
            {{ t("components.companies.discounts.add") }}
          </y-btn-nd>
          <p
            v-else
            class="text-h6 bg-pink-4 elevation-2 cursor-pointer rounded-lg pa-4"
            @click="selectDiscount(index)"
          >
            <span v-if="discount.type === 'unit'">
              {{ t("components.companies.discounts.free_units", { count: discount.value }) }}
            </span>
            <span v-else>
              {{
                t("components.companies.discounts.value_with_type", {
                  value: discount.value,
                  type: discount.type,
                })
              }}
            </span>
            {{ discount.product }}
            {{
              discount.appliedUntil
                ? t("components.companies.discounts.until") +
                  " " +
                  t("components.companies.discounts.applied_to_date", {
                    date: d(new Date(discount.appliedUntil), "short"),
                  })
                : t("components.companies.discounts.forever")
            }}
          </p>
        </div>
      </div>
      <div class="d-flex justify-end">
        <y-btn
          :disabled="!unsavedChanges"
          :loading="isLoading"
          text="Save"
          class="mt-6"
          @click="save"
        />
      </div>
      <v-expansion-panels v-if="archivedDiscounts.length > 0" class="mt-6">
        <v-expansion-panel>
          <v-expansion-panel-title class="font-italic bg-grey-lighten-3">
            {{ t("components.companies.discounts.archived") }}
          </v-expansion-panel-title>
          <v-expansion-panel-text class="pt-2">
            <div class="d-flex ga-5 flex-wrap">
              <div
                v-for="(archivedDiscount, index) in archivedDiscounts"
                :key="index"
                cols="12"
                sm="6"
                md="4"
                lg="3"
                class="bg-grey-lighten-3 rounded-lg pa-4 my-2"
              >
                <p class="text-h5">
                  <span v-if="archivedDiscount.type === 'unit'">
                    {{
                      t("components.companies.discounts.free_units", {
                        count: archivedDiscount.value,
                      })
                    }}
                  </span>
                  <span v-else>
                    {{
                      t("components.companies.discounts.value_with_type", {
                        value: archivedDiscount.value,
                        type: archivedDiscount.type,
                      })
                    }}
                  </span>
                  {{ archivedDiscount.product }}
                  {{
                    archivedDiscount.appliedUntil
                      ? t("components.companies.discounts.until") +
                        " " +
                        t("components.companies.discounts.applied_to_date", {
                          date: d(new Date(archivedDiscount.appliedUntil), "short"),
                        })
                      : t("components.companies.discounts.forever")
                  }}
                </p>
              </div>
            </div>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>
  </section>
</template>

<script setup lang="ts">
import {
  DealRates,
  DiscountTypeEnum,
  DiscountProductEnum,
  Discount,
  MerchantUpdateRatesRequest,
} from "@tableyeti/merchant-service";
import { isEqual, cloneDeep, pick } from "lodash-es";
import { computed, ref, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";

const { dealRates } = defineProps<{ dealRates: DealRates; isLoading: boolean }>();
const { t, d } = useI18n();

const localDiscounts = ref<Discount[]>([]);
onMounted(() => {
  localDiscounts.value = cloneDeep(dealRates.discounts ?? []);
});
watch(
  () => dealRates.discounts,
  (newVal) => {
    localDiscounts.value = cloneDeep(newVal ?? []);
  },
);

const addNewDiscount = ref(false);
const editDiscountIndex = ref<number | undefined>(undefined);
const discountForm = ref<Discount>({
  type: "unit",
  value: 0,
  product: "device",
  appliedUntil: "",
});
const discountFormValue = computed(() => ({
  type: discountForm.value.type,
  value: discountForm.value.value,
  product: discountForm.value.product,
  appliedUntil: discountForm.value.appliedUntil || undefined,
}));

const isDiscountArchived = (discount: Discount) =>
  discount.appliedUntil && new Date(discount.appliedUntil) < new Date();
const discounts = computed(() => [...(localDiscounts.value ?? []), undefined]);
const archivedDiscounts = computed(() => localDiscounts.value?.filter(isDiscountArchived) ?? []);
const unsavedChanges = computed(() => !isEqual(localDiscounts.value, dealRates.discounts ?? []));

const clearSelection = () => {
  editDiscountIndex.value = undefined;
  addNewDiscount.value = false;
};
const selectDiscount = (index: number) => {
  discountForm.value = cloneDeep(discounts.value[index]) ?? {
    type: "unit",
    value: 0,
    product: "device",
    appliedUntil: "",
  };
  editDiscountIndex.value = index;
  addNewDiscount.value = index === undefined;
};
const updateDiscount = (index: number) => {
  if (addNewDiscount.value) {
    localDiscounts.value.push(discountFormValue.value);
  } else {
    localDiscounts.value[index] = discountFormValue.value;
  }
  addNewDiscount.value = false;
  editDiscountIndex.value = undefined;
};
const deleteDiscount = (index: number) => {
  localDiscounts.value.splice(index, 1);
  editDiscountIndex.value = undefined;
};
const save = () => {
  emit(
    "update",
    pick(
      { ...dealRates, discounts: localDiscounts.value },
      "transactionRates",
      "deviceRate",
      "settlementRate",
      "discounts",
    ),
  );
};
const emit = defineEmits<{
  update: [dealRates: MerchantUpdateRatesRequest];
}>();
</script>
